import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { searchPlaces, getCurrentLocation, getNearbyPlaces } from "../../functions/location";
import "./style.css";

export default function LocationPickerModal({ onClose, onSelectLocation, selectedLocation = null }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState(selectedLocation);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const { user } = useSelector((state) => ({ ...state })) || { user: null };
  const [recentLocations] = useState([
    { 
      id: 1, 
      name: "Home", 
      address: "123 Oak Avenue, Downtown, NY", 
      lat: 40.7128, 
      lng: -74.0060,
      type: "home"
    },
    { 
      id: 2, 
      name: "Work Office", 
      address: "456 Business Center, Midtown, NY", 
      lat: 40.7589, 
      lng: -73.9851,
      type: "work"
    },
    { 
      id: 3, 
      name: "Fitness Center", 
      address: "789 Health Street, Uptown, NY", 
      lat: 40.7505, 
      lng: -73.9934,
      type: "gym"
    },
    { 
      id: 4, 
      name: "Local Coffee Shop", 
      address: "321 Brew Avenue, Downtown, NY", 
      lat: 40.7484, 
      lng: -73.9857,
      type: "cafe"
    },
    { 
      id: 5, 
      name: "City Park", 
      address: "654 Park Boulevard, Central, NY", 
      lat: 40.7527, 
      lng: -73.9772,
      type: "park"
    }
  ]);

  // Real place search function using API
  const searchPlacesAPI = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    
    try {
      if (!user || !user.token) {
        console.error("User token not available");
        setSearchResults([]);
        return;
      }
      
      const response = await searchPlaces(query, user.token);
      if (response && response.status === "ok" && Array.isArray(response.data)) {
        setSearchResults(response.data);
      } else {
        console.error("Error searching places:", response?.message || "Unknown error");
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error searching places:", error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Get current location using the imported function
  const getCurrentLocationData = useCallback(async () => {
    try {
      setLoading(true);
      const location = await getCurrentLocation();
      setCurrentLocation(location);
      
      // Get nearby places based on current location
      if (location && location.lat && location.lng && user && user.token) {
        try {
          const nearbyResponse = await getNearbyPlaces(location.lat, location.lng, 5000, user.token);
          if (nearbyResponse && nearbyResponse.status === "ok" && Array.isArray(nearbyResponse.data)) {
            setNearbyPlaces(nearbyResponse.data);
          } else {
            console.error("Error getting nearby places:", nearbyResponse?.message || "Unknown error");
            setNearbyPlaces([]);
          }
        } catch (error) {
          console.error("Error getting nearby places:", error);
          setNearbyPlaces([]);
        }
      }
    } catch (error) {
      console.error('Error getting current location:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.token) {
      getCurrentLocationData();
    }
  }, [user, getCurrentLocationData]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim() && user && user.token) {
        searchPlacesAPI(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, user, searchPlacesAPI]);

  const handleLocationSelect = (location) => {
    setSelected(location);
  };

  const handleDone = () => {
    console.log("Location selected:", selected);
    console.log("Location data being passed:", {
      name: selected?.name,
      address: selected?.address,
      lat: selected?.lat,
      lng: selected?.lng,
      type: selected?.type
    });
    onSelectLocation(selected);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleRemoveLocation = () => {
    setSelected(null);
    onSelectLocation(null);
    onClose();
  };

  const getLocationIcon = (type) => {
    switch (type) {
      case 'restaurant':
        return '🍽️';
      case 'cafe':
        return '☕';
      case 'park':
        return '🌳';
      case 'shopping':
        return '🛍️';
      case 'library':
        return '📚';
      case 'current':
        return '📍';
      case 'home':
        return '🏠';
      case 'work':
        return '🏢';
      case 'gym':
        return '💪';
      case 'recent':
        return '🏢';
      default:
        return '🏢';
    }
  };

  return (
    <div className="location_picker_modal">
      <div className="location_picker_modal_content">
        <div className="location_picker_modal_header">
          <h3>Add Location</h3>
          <button className="close_btn" onClick={handleCancel}>
            <i className="exit_icon"></i>
          </button>
        </div>
        
        <div className="location_picker_search">
          <div className="search_wrap">
            <i className="maps_icon"></i>
            <input
              type="text"
              placeholder="Search for a place..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {loading && (
              <div className="search_loading">
                <div className="spinner">
                  <div className="bounce1"></div>
                  <div className="bounce2"></div>
                  <div className="bounce3"></div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="location_picker_content">
          {selected && (
            <div className="selected_location">
              <div className="selected_location_header">
                <span>Selected Location</span>
                <button className="remove_btn" onClick={handleRemoveLocation}>
                  Remove
                </button>
              </div>
              <div className="selected_location_item">
                <div className="location_icon">
                  {getLocationIcon(selected.type)}
                </div>
                <div className="location_info">
                  <span className="location_name">{selected.name}</span>
                  <span className="location_address">{selected.address}</span>
                </div>
              </div>
            </div>
          )}

          {/* Current Location */}
          {currentLocation && (
            <div className="current_location">
              <h4>Current Location</h4>
              <div className="location_list">
                <div 
                  className={`location_item ${selected?.id === currentLocation.id ? 'selected' : ''}`}
                  onClick={() => handleLocationSelect(currentLocation)}
                >
                  <div className="location_icon">
                    {getLocationIcon(currentLocation.type)}
                  </div>
                  <div className="location_info">
                    <span className="location_name">{currentLocation.name}</span>
                    <span className="location_address">{currentLocation.address}</span>
                  </div>
                  {selected?.id === currentLocation.id && (
                    <div className="selected_checkmark">
                      <i className="check_icon"></i>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Search Results */}
          {searchResults && searchResults.length > 0 && (
            <div className="search_results">
              <h4>Search Results</h4>
              <div className="location_list">
                {searchResults.map((location) => (
                  <div 
                    key={location.id} 
                    className={`location_item ${selected?.id === location.id ? 'selected' : ''}`}
                    onClick={() => handleLocationSelect(location)}
                  >
                    <div className="location_icon">
                      {getLocationIcon(location.type)}
                    </div>
                    <div className="location_info">
                      <span className="location_name">{location.name}</span>
                      <span className="location_address">{location.address}</span>
                    </div>
                    {selected?.id === location.id && (
                      <div className="selected_checkmark">
                        <i className="check_icon"></i>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nearby Places */}
          {nearbyPlaces && nearbyPlaces.length > 0 && (
            <div className="nearby_places">
              <h4>Nearby Places</h4>
              <div className="location_list">
                {nearbyPlaces.map((location) => (
                  <div 
                    key={location.id} 
                    className={`location_item ${selected?.id === location.id ? 'selected' : ''}`}
                    onClick={() => handleLocationSelect(location)}
                  >
                    <div className="location_icon">
                      {getLocationIcon(location.type)}
                    </div>
                    <div className="location_info">
                      <span className="location_name">{location.name}</span>
                      <span className="location_address">{location.address}</span>
                      <span className="location_distance">{location.distance}m away</span>
                    </div>
                    {selected?.id === location.id && (
                      <div className="selected_checkmark">
                        <i className="check_icon"></i>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Locations */}
          <div className="recent_locations">
            <h4>Recent Locations</h4>
            <div className="location_list">
              {recentLocations.map((location) => (
                <div 
                  key={location.id} 
                  className={`location_item ${selected?.id === location.id ? 'selected' : ''}`}
                  onClick={() => handleLocationSelect(location)}
                >
                  <div className="location_icon">
                    {getLocationIcon(location.type)}
                  </div>
                  <div className="location_info">
                    <span className="location_name">{location.name}</span>
                    <span className="location_address">{location.address}</span>
                  </div>
                  {selected?.id === location.id && (
                    <div className="selected_checkmark">
                      <i className="check_icon"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* No Results */}
          {searchQuery && searchResults && searchResults.length === 0 && !loading && (
            <div className="no_results">
              <span>No places found for "{searchQuery}"</span>
              <p>Try searching for a different place or check your spelling.</p>
            </div>
          )}
        </div>

        <div className="location_picker_modal_footer">
          <button className="cancel_btn" onClick={handleCancel}>
            Cancel
          </button>
          <button className="done_btn" onClick={handleDone} disabled={!selected}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
} 